import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function TemplateDocsList() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await authFetch('/api/v1/template-docs');
        if (response.ok) {
          const data = await response.json();
          setTemplates(data);
        } else {
          setError('Failed to fetch templates');
        }
      } catch (error) {
        setError('Error fetching templates: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [authFetch]);

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот шаблон? Это действие нельзя отменить.')) {
      return;
    }

    setDeletingTemplate(templateId);
    try {
      const response = await authFetch(`/api/v1/template-docs/${templateId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove the deleted template from the list
        setTemplates(templates.filter(template => template.id !== templateId));
      } else {
        setError('Ошибка при удалении шаблона');
      }
    } catch (error) {
      setError('Ошибка при удалении шаблона: ' + error.message);
    } finally {
      setDeletingTemplate(null);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`container mt-4${isMobile ? ' p-0' : ''}`}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Шаблоны документов</h2>
        <Link to="/upload-sign-doc" className="btn btn-success">
          <i className="bi bi-plus-lg me-2"></i>
          Загрузить документ
        </Link>
      </div>
      <div className="card">
        <div className="card-body">
          {isMobile ? (
            <div className="d-flex flex-column gap-3">
              {templates.map((template) => (
                <div key={template.id} className="border rounded p-3 bg-light">
                  <div className="fw-bold mb-1">{template.name}</div>
                  <div className="small text-muted mb-1">Создан: {new Date(template.created_at).toLocaleDateString()}</div>
                  <div className="small text-muted mb-1">Обновлён: {new Date(template.updated_at).toLocaleDateString()}</div>
                  <div className="mb-2">Поля: {template.sign_doc_fields?.length || 0}</div>
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => navigate(`/template-docs/${template.id}`)}
                    >
                      Просмотр
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteTemplate(template.id)}
                      disabled={deletingTemplate === template.id}
                    >
                      {deletingTemplate === template.id ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Удаление...
                        </>
                      ) : (
                        'Удалить'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Дата создания</th>
                    <th>Дата обновления</th>
                    <th>Количество полей</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr key={template.id}>
                      <td>{template.name}</td>
                      <td>{new Date(template.created_at).toLocaleString()}</td>
                      <td>{new Date(template.updated_at).toLocaleString()}</td>
                      <td>{template.sign_doc_fields?.length || 0}</td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => navigate(`/template-docs/${template.id}`)}
                        >
                          Просмотр
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteTemplate(template.id)}
                          disabled={deletingTemplate === template.id}
                        >
                          {deletingTemplate === template.id ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              Удаление...
                            </>
                          ) : (
                            'Удалить'
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplateDocsList; 